import { truncateString } from '@/shared/utils';

import type { TRainValueJs } from '../../utils/value';

/**
 * Sanity bound, not the visible limit — the row clamps in CSS, which keeps the
 * full argument in the DOM for its overflow tooltip. Truncating tighter here
 * would make that tooltip show an ellipsis as if it were the whole value.
 */
export const MAX_ARG_CHARS = 240;

/**
 * An argument as a single line, or `undefined` when there is nothing to show
 * (wrong type, absent, blank) — which the row reads as "no label yet".
 *
 * @param value The decoded tool argument.
 */
export const asLine = (value: TRainValueJs | undefined): string | undefined => {
  if (typeof value !== 'string') return undefined;
  const collapsed = value.replace(/\s+/g, ' ').trim();
  return collapsed || undefined;
};

/**
 * An argument echoed inside a row, single-line and bounded.
 *
 * @param value The decoded tool argument.
 */
export const asShortLabel = (
  value: TRainValueJs | undefined,
): string | undefined => {
  const line = asLine(value);
  return line && truncateString(line, MAX_ARG_CHARS, '…');
};

/**
 * A URL as the site it points at — the host is the recognisable part. Falls
 * back to the raw string when it doesn't parse; that is still what was fetched.
 *
 * @param value The decoded URL argument.
 */
export const asHostLabel = (
  value: TRainValueJs | undefined,
): string | undefined => {
  const line = asLine(value);
  if (!line) return undefined;
  try {
    const { hostname } = new URL(line);
    // `www.` is noise, and it costs row width.
    const site = hostname.replace(/^www\./, '');
    if (site) return truncateString(site, MAX_ARG_CHARS, '…');
  } catch {
    // Not an absolute URL — show what we were given.
  }
  return truncateString(line, MAX_ARG_CHARS, '…');
};
